import { useState } from "react";
import Card from "../../../components/core/basic/Card";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { ButtonGroup } from "../../../components/core/basic/ButtonGroup";
import { Chart } from "@highcharts/react";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import { trellisPalette } from "../../../components/custom/analytics/palettes";
import { useAppSelector } from "../../../redux/hooks";
import Loading from "../../../components/core/blocks/Loading";
import { type GVAdvertisementSummary } from "../../../zodios/apiDefinitions/api/liveDashboard";
import { Link } from "@tanstack/react-router";
import { css } from "../../../../styled-system/css";
import { PerformanceProgressBars } from "./PerformanceProgressBars";
import BudgetIncreaseModal from "./BudgetIncreaseModal";
import { useLiveAdReport } from "../api/useLiveAdReport";
import { useLiveDashboardFilters } from "../api/useLiveDashboardFilters";
import { useTranslation } from "react-i18next";

const AD_TYPES_COLORS = {
  SBV: trellisPalette[5],
  SB: trellisPalette[4],
  SP: trellisPalette[3],
  SD: trellisPalette[2],
  // Not sure where U comes from, but this keeps TS happy
  U: trellisPalette[1],
};

const AdTypePieChart = () => {
  const { t } = useTranslation(["live"]);
  const filters = useLiveDashboardFilters();
  const adReportQuery = useLiveAdReport(filters);

  const AD_TYPES: Record<"SBV" | "SB" | "SP" | "SD", string> = {
    SBV: t(($) => $.live["Sponsored Brands Video"]),
    SB: t(($) => $.live["Sponsored Brands"]),
    SP: t(($) => $.live["Sponsored Products"]),
    SD: t(($) => $.live["Sponsored Display"]),
  };

  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const adTypes = Object.keys(adReportQuery.data?.spend_by_type ?? {}) as Array<keyof typeof AD_TYPES_COLORS>;

  let total = 0;

  const adTypeData = adTypes
    .filter((adType) => adType !== "U")
    .map((adType) => {
      total += adReportQuery.data?.spend_by_type[adType] || 0;
      return {
        name: AD_TYPES[adType],
        y: adReportQuery.data?.spend_by_type[adType],
        color: AD_TYPES_COLORS[adType],
      };
    });

  const options: Highcharts.Options = {
    chart: {
      type: "pie",
      style: {
        fontFamily: "Poppins",
        fontWeight: "300",
        fontSize: "15px",
      },
      height: "400px",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          useHTML: true,
          formatter: function () {
            return (
              `<span style="font-size: 1.2em; font-weight: 600; opacity: 0.8">${this.name}</span><br>` +
              `<span style="opacity: 0.6; margin: auto; display: block; text-align: center; width: 100%;">${formatCurrency(
                this.y,
                {
                  marketPlace: marketPlace,
                },
              )} - ${formatPercent((this.y ?? 0) / total, {
                decimalPlaces: 1,
              })}</span>`
            );
          },
        },
      },
    },
    tooltip: {
      useHTML: true,
      formatter: function () {
        const formattedValue = formatCurrency(this.y, {
          marketPlace: marketPlace,
        });

        if (total) {
          return (
            "<span><strong>" +
            this.name +
            ":</strong><br />" +
            formattedValue +
            "<strong>" +
            ` (${formatPercent((this.y ?? 0) / total, {
              decimalPlaces: 1,
            })})` +
            "</strong></span>"
          );
        }

        return "<span><strong>" + this.name + ":</strong><br />" + formattedValue + "</span>";
      },
    },
    series: [
      {
        type: "pie",
        name: t(($) => $.live["Ad Type"]),
        data: adTypeData,
      },
    ],
  };

  if (adReportQuery.isLoading) {
    return <Loading fullPage={false} height={"400px"} />;
  }

  return <Chart options={options} />;
};

const Ad = ({ ad }: { ad: GVAdvertisementSummary }) => {
  const { t } = useTranslation(["live"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  return (
    <div
      className={css({
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        width: "100%",
        fontSize: "medium",
      })}
    >
      <VStack css={{ alignItems: "flex-start", textAlign: "left" }}>
        <Link
          className={css({
            cursor: "pointer",
            textDecoration: "none",
            fontSize: "large",
          })}
          to="/user/advertising/dashboard/ads/$id"
          params={{ id: String(ad.ad_id) }}
          target="_blank"
        >
          {ad.ad_name}
        </Link>
        <small>
          {t(($) => $.live["Spend / Budget ({budget})"], {
            budget: formatCurrency(ad.daily_budget, { marketPlace }),
          })}
          {ad.daily_budget_override ?
            " " +
            t(($) => $.live["- increased today to {amount}"], {
              amount: formatCurrency(ad.daily_budget_override, { marketPlace }),
            })
          : null}
        </small>
        <PerformanceProgressBars
          current={ad.spend}
          previous={ad.daily_budget}
          format={(v) => formatCurrency(v, { marketPlace })}
          single
        />
      </VStack>
      <div className={css({ paddingX: "large" })}>
        <Flex className={css({ justifyContent: "space-between", paddingX: "large" })}>
          <VStack gap={0}>
            <small className={css({ fontWeight: "bolder" })}>{t(($) => $.live["Sales"])}</small>
            {formatCurrency(ad.sales, { marketPlace })}
          </VStack>
          <VStack gap={0}>
            <small className={css({ fontWeight: "bolder" })}>{t(($) => $.live["ACOS"])}</small>
            {ad.sales > 0 ?
              formatPercent(ad.spend / ad.sales, {
                decimalPlaces: 0,
              })
            : formatPercent(0, {
                decimalPlaces: 0,
              })
            }
          </VStack>
        </Flex>
        <BudgetIncreaseModal
          ad={ad}
          defaultSuggestedBudget={Math.ceil(ad.daily_budget * 1.2)}
          isUnmanagedAd={ad.ad_goal === 25 && !ad.non_trellis_enrolled_ad_accept_bids}
        />
      </div>
    </div>
  );
};

const CampaignPerformanceSection = () => {
  const { t } = useTranslation(["live"]);
  const [campaignSelectedBucket, setCampaignSelectedBucket] = useState("highest_spending_campaigns");
  const filters = useLiveDashboardFilters();
  const adReportQuery = useLiveAdReport(filters);

  return (
    <Card>
      <Flex justifyContent={"center"}>
        <ButtonGroup
          buttons={[
            {
              label: t(($) => $.live["Highest Spending Campaigns"]),
              value: "highest_spending_campaigns",
            },
            {
              label: t(($) => $.live["Spend by Campaign Type"]),
              value: "spend_by_campaign_type",
            },
          ]}
          value={campaignSelectedBucket}
          onSelect={(values) => {
            setCampaignSelectedBucket(values);
          }}
        />
      </Flex>
      {campaignSelectedBucket === "highest_spending_campaigns" && (
        <VStack css={{ gap: "large", paddingY: "large" }}>
          {adReportQuery.isLoading ?
            <Loading fullPage={false} height={"400px"} />
          : <>
              {adReportQuery.data?.ad_results.map((ad) => {
                return <Ad key={ad.ad_id} ad={ad} />;
              })}
            </>
          }
        </VStack>
      )}
      {campaignSelectedBucket === "spend_by_campaign_type" && <AdTypePieChart />}
    </Card>
  );
};

export default CampaignPerformanceSection;
